"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, type ReactNode } from "react";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { getRankHoverBorderClass, getRankStaticBorderClass } from "@/components/waves/drops/dropRankStyles";
import { AdditionalActionPromiseBadge } from "@/components/waves/drops/AdditionalActionPromiseBadge";
import type { DropInteractionParams } from "@/components/waves/drops/drop.types";
import { DropLocation, hasDropFooter } from "@/components/waves/drops/drop.types";
import useIsMobileDevice from "@/hooks/isMobileDevice";
import WaveDropActions from "@/components/waves/drops/WaveDropActions";
import MemeWinnerHeader from "./MemeWinnerHeader";
import MemeWinnerDescription from "./MemeWinnerDescription";
import MemeWinnerArtistInfo from "./MemeWinnerArtistInfo";
import MemeDropTraits from "./MemeDropTraits";
import MemeDropVoteStats from "./meme-participation-drop/MemeDropVoteStats";
import DropMobileMenuHandler from "@/components/waves/drops/DropMobileMenuHandler";
import DropListItemContentMedia from "@/components/drops/view/item/content/media/DropListItemContentMedia";
import { useDropContext } from "@/components/waves/drops/DropContext";
import { WaveWinnerIdentity } from "@/components/waves/winners/identity/WaveWinnerIdentity";
interface MemeWinnerDropProps {
  readonly drop: ExtendedDrop;
  readonly showReplyAndQuote: boolean;
  readonly onReply: (param: DropInteractionParams) => void;
  readonly footer?: ReactNode;
  readonly showInteractions?: boolean | undefined;
}
const getRankHoverClass = (rank: number | null): string => {
  return getRankHoverBorderClass(rank);
};
const getNonEmptyText = (value: string | null | undefined): string | undefined => {
  const trimmed = value?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : undefined;
};
const getMetadataValue = (drop: ExtendedDrop, dataKey: string): string | undefined => getNonEmptyText(drop.metadata.find(metadata => metadata.data_key === dataKey)?.data_value);
export default function MemeWinnerDrop(t0) {
  const $ = _c(64);
  const {
    drop,
    showReplyAndQuote,
    onReply,
    footer,
    showInteractions: t1
  } = t0;
  const showInteractions = t1 === undefined ? true : t1;
  const isMobile = useIsMobileDevice();
  const {
    location
  } = useDropContext();
  let t2;
  if ($[0] !== drop) {
    t2 = getNonEmptyText(drop.title) ?? getMetadataValue(drop, "title") ?? "Artwork Title";
    $[0] = drop;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const title = t2;
  let t3;
  if ($[2] !== drop) {
    t3 = getNonEmptyText(drop.parts.at(0)?.content) ?? getMetadataValue(drop, "description") ?? "This is an artwork submission for The Memes collection.";
    $[2] = drop;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const description = t3;
  let t4;
  if ($[4] !== drop.parts) {
    t4 = drop.parts.at(0)?.media.at(0);
    $[4] = drop.parts;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const artworkMedia = t4;
  let t5;
  if ($[6] !== title) {
    t5 = <MemeWinnerHeader title={title} />;
    $[6] = title;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== drop.is_additional_action_promised) {
    t6 = drop.is_additional_action_promised === true && <AdditionalActionPromiseBadge />;
    $[8] = drop.is_additional_action_promised;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t5 || $[11] !== t6) {
    t7 = <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">{t5}{t6}</div>;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const headerContent = t7;
  let t8;
  if ($[13] !== drop || $[14] !== onReply) {
    t8 = () => {
      onReply({
        drop,
        partId: drop.parts[0]?.part_id
      });
    };
    $[13] = drop;
    $[14] = onReply;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const handleOnReply = t8;
  const effectiveRank = drop.winning_context?.place ?? drop.rank;
  let t9;
  if ($[16] !== drop) {
    t9 = <div className="tw-border-x-0 tw-border-b tw-border-t-0 tw-border-solid tw-border-white/5 tw-bg-iron-900/30 tw-p-4 tw-pb-3"><MemeWinnerArtistInfo drop={drop} /></div>;
    $[16] = drop;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== description) {
    t10 = <MemeWinnerDescription description={description} />;
    $[18] = description;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== headerContent || $[21] !== t10) {
    t11 = <div className="tw-px-4 tw-pb-4 tw-pt-4"><div className="tw-space-y-1">{headerContent}{t10}</div></div>;
    $[20] = headerContent;
    $[21] = t10;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== drop) {
    t12 = <WaveWinnerIdentity drop={drop} variant="full" className="tw-px-4 tw-pb-4" />;
    $[23] = drop;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== artworkMedia) {
    t13 = artworkMedia && <div className="tw-mx-0.5 tw-flex tw-h-96 tw-justify-center tw-bg-iron-950"><DropListItemContentMedia media_mime_type={artworkMedia.mime_type} media_url={artworkMedia.url} isCompetitionDrop={true} /></div>;
    $[25] = artworkMedia;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== drop) {
    t14 = <div className="tw-mt-4 tw-hidden tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-white/5 tw-bg-iron-900/30 tw-p-4 lg:tw-block"><MemeDropTraits drop={drop} /></div>;
    $[27] = drop;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== t11 || $[30] !== t12 || $[31] !== t13 || $[32] !== t14 || $[33] !== t9) {
    t15 = <>{t9}{t11}{t12}{t13}{t14}</>;
    $[29] = t11;
    $[30] = t12;
    $[31] = t13;
    $[32] = t14;
    $[33] = t9;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  const content = t15;
  const t16 = `tw-w-full ${location === DropLocation.WAVE ? "tw-px-4 tw-py-1" : ""} tw-group tw-relative`;
  let t17;
  if ($[35] !== effectiveRank) {
    t17 = getRankStaticBorderClass(effectiveRank);
    $[35] = effectiveRank;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== effectiveRank) {
    t18 = getRankHoverClass(effectiveRank);
    $[37] = effectiveRank;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  const t19 = `tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid ${t17} tw-transition-all tw-duration-200 tw-ease-out ${location === DropLocation.WAVE ? "tw-bg-iron-900/80" : "tw-bg-iron-950"} ${t18}`;
  let t20;
  if ($[39] !== content || $[40] !== drop || $[41] !== handleOnReply || $[42] !== showInteractions || $[43] !== showReplyAndQuote) {
    t20 = showInteractions ? <DropMobileMenuHandler drop={drop} showReplyAndQuote={showReplyAndQuote} onReply={handleOnReply}>{content}</DropMobileMenuHandler> : content;
    $[39] = content;
    $[40] = drop;
    $[41] = handleOnReply;
    $[42] = showInteractions;
    $[43] = showReplyAndQuote;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== drop) {
    t21 = <div className="tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-white/5 tw-bg-iron-900/30 tw-px-4 tw-py-4"><MemeDropVoteStats drop={drop} /></div>;
    $[45] = drop;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  let t22;
  if ($[47] !== footer) {
    t22 = hasDropFooter(footer) && <div className="tw-px-4 tw-pb-4 tw-pt-2">{footer}</div>;
    $[47] = footer;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  let t23;
  if ($[49] !== drop || $[50] !== handleOnReply || $[51] !== isMobile || $[52] !== showInteractions || $[53] !== showReplyAndQuote) {
    t23 = !isMobile && showInteractions && showReplyAndQuote && <div className="tw-absolute tw-right-4 tw-top-2"><WaveDropActions drop={drop} activePartIndex={0} onReply={handleOnReply} /></div>;
    $[49] = drop;
    $[50] = handleOnReply;
    $[51] = isMobile;
    $[52] = showInteractions;
    $[53] = showReplyAndQuote;
    $[54] = t23;
  } else {
    t23 = $[54];
  }
  let t24;
  if ($[55] !== t19 || $[56] !== t20 || $[57] !== t21 || $[58] !== t22 || $[59] !== t23) {
    t24 = <div className={t19}>{t20}{t21}{t22}{t23}</div>;
    $[55] = t19;
    $[56] = t20;
    $[57] = t21;
    $[58] = t22;
    $[59] = t23;
    $[60] = t24;
  } else {
    t24 = $[60];
  }
  let t25;
  if ($[61] !== t16 || $[62] !== t24) {
    t25 = <div className="tw-mb-3 tw-w-full"><div className={t16}>{t24}</div></div>;
    $[61] = t16;
    $[62] = t24;
    $[63] = t25;
  } else {
    t25 = $[63];
  }
  return t25;
}